/**
 * Script Preview Service
 *
 * Handles script preview generation for video and audio content.
 * Allows users to review, edit, approve, or regenerate scripts
 * before proceeding with full media generation.
 *
 * Token Billing:
 * - Requires authentication
 * - Free models (ending with :free) don't charge tokens
 * - Paid models are billed based on LLM usage
 */

import { getApiBaseUrl } from './urlResolver';
import { getAuthHeadersFromStorage, unwrapEnvelope } from '../utils/httpUtils';
import type {
  ScriptPreviewRequest,
  ScriptPreviewResponse,
  VideoConfig,
  AudioGenerationRequest,
} from '../types';

/**
 * Build a human-readable error message from a serverless API error payload.
 *
 * For Pydantic 422 validation errors the API nests field-level info under
 * `error.details.errors[]`. Surfacing those field/message pairs is the
 * difference between an actionable error in the Script Preview modal
 * ("body.duration_minutes: Input should be greater than or equal to 0.17")
 * and the opaque "Request validation failed" that hides the real mismatch.
 */
function _extractErrorMessage(errorData: unknown, status: number): string {
  const data = (errorData ?? {}) as Record<string, unknown>;
  const errorField = data.error;

  // Top-level error as string
  if (typeof errorField === 'string') return errorField;

  if (errorField && typeof errorField === 'object') {
    const errObj = errorField as Record<string, unknown>;
    const baseMessage =
      (typeof errObj.message === 'string' ? errObj.message : undefined) ??
      (typeof data.message === 'string' ? (data.message as string) : undefined);

    // Pydantic validation: include field-level details so the user sees WHICH
    // field failed (currently mostly duration_minutes when the FE default of
    // 0.25 min slips through to the API).
    const details = errObj.details as Record<string, unknown> | undefined;
    const fieldErrors = details?.errors;
    if (Array.isArray(fieldErrors) && fieldErrors.length > 0) {
      const formatted = fieldErrors
        .map((fe) => {
          const f = fe as Record<string, unknown>;
          const field = typeof f.field === 'string' ? f.field : undefined;
          const msg = typeof f.message === 'string' ? f.message : 'invalid value';
          return field ? `${field}: ${msg}` : msg;
        })
        .join('; ');
      return baseMessage ? `${baseMessage} — ${formatted}` : formatted;
    }

    if (baseMessage) return baseMessage;
  }

  if (typeof data.message === 'string') return data.message;
  return `HTTP error! status: ${status}`;
}

/**
 * Script Preview API Service
 */
export class ScriptPreviewService {
  /**
   * Generate a script preview for user review
   *
   * @param request - Preview request with topic, duration, language, etc.
   * @returns Script preview response with preview_id and generated script
   */
  static async generatePreview(request: ScriptPreviewRequest): Promise<ScriptPreviewResponse> {
    const baseUrl = getApiBaseUrl();
    const authHeaders = await getAuthHeadersFromStorage();

    const response = await fetch(`${baseUrl}/api/v1/generate/script-preview`, {
      method: 'POST',
      headers: authHeaders,
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      // Handle specific error codes
      if (response.status === 401) {
        throw new Error('Authentication required for script preview generation');
      }
      if (response.status === 402) {
        throw new Error(errorData.message || 'Insufficient tokens for script preview');
      }
      throw new Error(_extractErrorMessage(errorData, response.status));
    }

    // API returns canonical {success, data} envelope — script_preview.py uses success_response()
    const responseData = await response.json();
    return unwrapEnvelope<ScriptPreviewResponse>(responseData);
  }

  /**
   * Get a previously generated script preview
   *
   * @param previewId - The preview ID from generatePreview
   * @returns Script preview response or null if not found
   */
  static async getPreview(previewId: string): Promise<ScriptPreviewResponse | null> {
    const baseUrl = getApiBaseUrl();

    const response = await fetch(`${baseUrl}/api/v1/generate/script-preview/${previewId}`, {
      method: 'GET',
      headers: await getAuthHeadersFromStorage(),
    });

    if (response.status === 404) {
      return null;
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(_extractErrorMessage(errorData, response.status));
    }

    // API returns canonical {success, data} envelope — script_preview.py uses success_response()
    const responseData = await response.json();
    return unwrapEnvelope<ScriptPreviewResponse>(responseData);
  }

  /**
   * Regenerate a script preview with the same parameters
   *
   * @param previewId - The preview ID to regenerate
   * @returns New script preview response with a new preview_id
   */
  static async regeneratePreview(previewId: string): Promise<ScriptPreviewResponse> {
    const baseUrl = getApiBaseUrl();
    const authHeaders = await getAuthHeadersFromStorage();

    const response = await fetch(
      `${baseUrl}/api/v1/generate/script-preview/${previewId}/regenerate`,
      {
        method: 'POST',
        headers: authHeaders,
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      // Handle specific error codes
      if (response.status === 401) {
        throw new Error('Authentication required for script regeneration');
      }
      if (response.status === 402) {
        throw new Error(errorData.message || 'Insufficient tokens for script regeneration');
      }
      throw new Error(_extractErrorMessage(errorData, response.status));
    }

    // API returns canonical {success, data} envelope — script_preview.py uses success_response()
    const responseData = await response.json();
    return unwrapEnvelope<ScriptPreviewResponse>(responseData);
  }

  /**
   * Helper: Create preview request from video config
   */
  static createVideoPreviewRequest(config: VideoConfig): ScriptPreviewRequest {
    return {
      generation_type: 'video',
      topic: config.topic,
      duration_minutes: config.duration_minutes,
      language: config.language,
      style: config.style,
      resolution: config.resolution,
      llm_provider: config.llm_provider,
      llm_model: config.llm_model,
    };
  }

  /**
   * Helper: Create preview request from audio config
   */
  static createAudioPreviewRequest(
    config: AudioGenerationRequest & { topic?: string }
  ): ScriptPreviewRequest {
    return {
      generation_type: 'audio',
      topic: config.topic || '',
      duration_seconds: config.duration_seconds,
      language: config.language,
      llm_provider: config.llm_provider,
      llm_model: config.llm_model,
    };
  }
}

export default ScriptPreviewService;
